<template>
  <!-- Reset password (US 9383): reached from the reset email, in the shared sign-in layout -->
  <auth-layout>
    <!-- Link expired or invalid -->
    <template v-if="step === 'expired'">
      <div class="lg-sent">
        <div class="lg-sent__icon lg-sent__icon--amber"><font-awesome-icon icon="hourglass-half"/></div>
        <h1 class="lg-title">{{ $t('reset_password.expired_title') }}</h1>
        <p class="lg-sub" qid="reset-password-error">{{ $t('reset_password.expired_text') }}</p>
      </div>
      <router-link to="/login?reset=true" class="lg-cta" qid="reset-request-new-link">
        {{ $t('reset_password.request_new_link') }}
      </router-link>
      <p class="lg-foot">
        <router-link to="/login">{{ $t('forgot_password.back_to_sign_in') }}</router-link>
      </p>
    </template>

    <!-- Account signs in with Microsoft or Google, so it has no password to reset -->
    <template v-if="step === 'sso'">
      <h1 class="lg-title">{{ $t('reset_password.set_title') }}</h1>
      <div class="lg-who">
        <div class="lg-who__avatar">{{ initials }}</div>
        <div class="lg-who__email">{{ form.email }}</div>
      </div>
      <div class="lg-org" :qid="'verify-' + ssoProvider + '-description'">
        <div class="lg-org__logo" :class="'lg-org__logo--' + ssoProvider">
          <font-awesome-icon :icon="['fab', ssoProvider]"/>
        </div>
        <div class="lg-org__title">{{ $t('login.sso_title_' + ssoProvider) }}</div>
        <div class="lg-org__text">{{ $t('login.sso_text') }}</div>
      </div>
      <button type="button"
              class="lg-cta"
              :qid="'login-' + ssoProvider + '-button'"
              @click="handleSsoLogin"
      >
        <font-awesome-icon :icon="['fab', ssoProvider]"/>
        {{ $t('login.sso_continue_' + ssoProvider) }}
      </button>
    </template>

    <!-- Choose a new password -->
    <template v-if="step === 'form'">
      <h1 class="lg-title">{{ $t('reset_password.set_title') }}</h1>
      <p class="lg-sub" qid="reset-password-description">{{ $t('reset_password.set_text') }}</p>

      <div v-if="getEmail" class="lg-who">
        <div class="lg-who__avatar">{{ initials }}</div>
        <div class="lg-who__email">{{ form.email }}</div>
      </div>

      <form @submit.prevent="onSubmit" qid="reset-password-form">
        <div v-if="!getEmail" class="lg-field">
          <label for="rp-email">{{ $t('login.work_email_label') }}</label>
          <div class="lg-input">
            <font-awesome-icon icon="envelope"/>
            <input id="rp-email"
                   type="email"
                   v-model.trim="form.email"
                   autocomplete="username"
                   required
                   :placeholder="$t('login.work_email_placeholder')"
                   qid="input-reset-email"
            >
          </div>
        </div>
        <!-- Hidden username field so password managers save the new password for this email -->
        <input v-else type="email" :value="form.email" autocomplete="username" class="d-none" tabindex="-1" aria-hidden="true">

        <div class="lg-field">
          <label for="rp-password">{{ $t('reset_password.password_placeholder') }}</label>
          <div class="lg-input" :class="{ 'lg-input--err': form.password && !validationPass && passwordTouched }">
            <font-awesome-icon icon="lock"/>
            <input id="rp-password"
                   ref="password"
                   :type="showPassword ? 'text' : 'password'"
                   v-model="form.password"
                   autocomplete="new-password"
                   required
                   qid="input-reset-password"
                   @blur="passwordTouched = true"
            >
            <button type="button"
                    class="lg-input__eye"
                    @click="showPassword = !showPassword"
                    :aria-label="showPassword ? $t('login.hide_password') : $t('login.show_password')"
                    :title="showPassword ? $t('login.hide_password') : $t('login.show_password')"
            >
              <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'"/>
            </button>
          </div>
          <div class="lg-hint"
               :class="{ 'lg-hint--err': form.password && !validationPass && passwordTouched, 'lg-hint--ok': validationPass }"
          >
            <font-awesome-icon :icon="validationPass ? 'check' : 'info-circle'"/>
            {{ $t('reset_password.notice.password_requirement') }}
          </div>
        </div>

        <div class="lg-field">
          <label for="rp-repeat">{{ $t('reset_password.repeat_password_placeholder') }}</label>
          <div class="lg-input" :class="{ 'lg-input--err': form.repeatPassword && !validationRepeatPass }">
            <font-awesome-icon icon="lock"/>
            <input id="rp-repeat"
                   :type="showPassword ? 'text' : 'password'"
                   v-model="form.repeatPassword"
                   autocomplete="new-password"
                   required
                   qid="input-reset-repeatPassword"
            >
          </div>
          <div v-if="form.repeatPassword && !validationRepeatPass" class="lg-hint lg-hint--err">
            <font-awesome-icon icon="exclamation-circle"/>{{ $t('reset_password.notice.do_not_match') }}
          </div>
          <div v-if="validationRepeatPass" class="lg-hint lg-hint--ok">
            <font-awesome-icon icon="check"/>{{ $t('reset_password.notice.looks_good') }}
          </div>
        </div>

        <div v-if="errorMessage" class="lg-alert lg-alert--red" qid="reset-password-error">
          <font-awesome-icon icon="exclamation-circle"/><div>{{ errorMessage }}</div>
        </div>

        <button type="submit"
                class="lg-cta"
                :disabled="buttonDisabled || !validationPass || !validationRepeatPass"
                qid="button-submit-reset-password"
        >
          <span v-if="buttonDisabled" class="lg-spin"></span>
          {{ $t('reset_password.button') }}
        </button>
      </form>
    </template>

    <!-- Password changed -->
    <template v-if="step === 'done'">
      <div class="lg-sent">
        <div class="lg-sent__icon"><font-awesome-icon icon="check"/></div>
        <h1 class="lg-title">{{ $t('reset_password.done_title') }}</h1>
        <p class="lg-sub">{{ $t('reset_password.done_text') }}</p>
      </div>
      <router-link to="/login" class="lg-cta" qid="reset-go-to-login">{{ $t('login.title') }}</router-link>
    </template>
  </auth-layout>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import AuthLayout from '@/components/auth/AuthLayout.vue'

export default {
  components: {
    AuthLayout
  },
  data: function() {
    return {
      // 'form', 'sso', 'expired' or 'done'
      step: 'form',
      form: {
        email: '',
        password: '',
        repeatPassword: '',
      },
      ssoProvider: 'microsoft',
      showPassword: false,
      passwordTouched: false,
      errorMessage: '',
      buttonDisabled: false,
    }
  },
  computed: {
    getToken() {
      return this.$route.params.token
    },
    getEmail() {
      return this.$route.query.email
    },
    initials() {
      const name = this.form.email.split('@')[0] || '';
      const parts = name.split(/[._-]+/).filter(Boolean);
      return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?';
    },
    validationPass() {
      let containsCapital = RegExp('[A-Z]');
      let containsSpecial = RegExp('\\W');

      return !!this.form.password && this.form.password.length > 7 && containsCapital.test(this.form.password) && containsSpecial.test(this.form.password);
    },
    validationRepeatPass() {
      return !!this.form.repeatPassword && this.form.password === this.form.repeatPassword;
    }
  },
  methods: {
    handleSsoLogin() {
      window.location.href = this.ssoProvider === 'microsoft' ? '/api/auth/azure' : '/api/auth/google';
    },
    // An expired or unknown link comes back as 400; password rule errors come back as 422
    isExpiredError(error) {
      return error.status === 400;
    },
    onSubmit() {
      this.buttonDisabled = true;
      this.errorMessage = '';
      // Keep the reference in a variable
      let self = this;

      piincHttp.post('password/reset', {
        email: this.form.email,
        password: this.form.password,
        password_confirmation: this.form.repeatPassword,
        token: this.getToken
      }).then(function() {
        self.buttonDisabled = false;
        // The login page opens with this email filled in
        localStorage.setItem('login_hint', JSON.stringify({ email: self.form.email.toLowerCase(), method: 'password' }));
        self.step = 'done';
      }, function(error) {
        self.buttonDisabled = false;
        if (self.isExpiredError(error)) {
          self.step = 'expired';
        } else {
          self.errorMessage = (error.data && error.data.message) || self.$t('login.error_message');
        }
      })
    },
  },
  // Lifecycle hooks
  created() {
    let self = this;

    if (this.getEmail) {
      this.form.email = this.getEmail

      piincHttp.get('users/reset/' + this.getToken + '/' + this.getEmail).then(function(response) {
        if (response.data.user.authentication == 'Microsoft') {
          self.ssoProvider = 'microsoft';
          self.step = 'sso';
        }
        if (response.data.user.authentication == 'Google') {
          self.ssoProvider = 'google';
          self.step = 'sso';
        }
      }, function() {
        self.step = 'expired';
      })
    }
  }
}
</script>
